import { Loader2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthApiError, loginRequest, useAuth } from "@/lib/auth";
import {
  clearMfaSession,
  rememberMfaSession,
} from "@/lib/auth/mfa-session";

function messageFor(err: AuthApiError): string {
  switch (err.code) {
    case "invalid_credentials":
      return "Email or password is incorrect.";
    case "account_disabled":
      return "This account has been disabled. Contact an administrator.";
    case "rate_limited":
      return "Too many attempts. Please wait a minute and try again.";
    case "server_misconfigured":
      return "Authentication is misconfigured on the server. Contact an administrator.";
    default:
      return err.serverMessage ?? "Sign-in failed. Please try again.";
  }
}

function safeNextParam(search: string): string {
  // Only allow same-origin paths. Reject protocol-relative ("//evil.com")
  // and anything that doesn't begin with "/".
  const next = new URLSearchParams(search).get("next");
  if (next && next.startsWith("/") && !next.startsWith("//")) return next;
  return "/";
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthed } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await loginRequest({ email: email.trim(), password });
      const next = safeNextParam(location.search);
      if (result.kind === "requires_2fa") {
        rememberMfaSession({ token: result.mfaSession, next });
        navigate("/login/2fa", { replace: true });
        return;
      }
      clearMfaSession();
      setAuthed({ accessToken: result.accessToken, user: result.user });
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError) {
        setError(messageFor(err));
      } else {
        setError("Couldn't reach the server. Check your connection and retry.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            Finance
          </p>
          <h1 className="text-xl font-semibold tracking-tight">
            Sign in to your account
          </h1>
        </div>

        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-sm normal-case tracking-normal text-foreground">
              Credentials
            </CardTitle>
            <CardDescription>
              Use the email an administrator invited you with.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/password-reset/request"
                    className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
              </div>

              {error ? (
                <p
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
                >
                  {error}
                </p>
              ) : null}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
